/* Shared collection, search and blog pagination. Native links also work without JS. */
(() => {
  if (customElements.get('infinite-scroll')) return;
  const restoredGrids = new Set();
  const busyGrids = new WeakMap();

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.button = this.querySelector('[data-load-more]');
      this.gridId = this.dataset.gridId || 'product-grid-ajax';
      this.paginationId = this.dataset.paginationId || 'pagination-ajax';
      this.grid = document.getElementById(this.gridId);
      this.pagination = document.getElementById(this.paginationId);
      if (!this.button || !this.grid || !this.pagination) return;
      this.initialized = true;
      this.nextUrl = this.dataset.nextUrl;
      this.initialNextUrl = this.nextUrl;
      this.initialCount = this.grid.children.length;
      this.status = this.querySelector('[data-load-status]');
      this.scope = this.closest('[data-ajax-parent]') || this.closest('.shopify-section');
      this.loadedUrls = new Set();
      this.onClick = event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.automaticPaused = false;
        this.fetchNextPage(true);
      };
      this.button.addEventListener('click', this.onClick);
      this.onPageHide = () => this.saveForBackNavigation();
      window.addEventListener('pagehide', this.onPageHide);
      this.restoreForBackNavigation();
      if (this.dataset.triggerType === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && !this.automaticPaused) this.fetchNextPage();
        }, { rootMargin: '0px 0px 160px 0px', threshold: 0 });
        if (this.nextUrl) this.observer.observe(this);
      }
      // Facets mark their current list as processing before replacing its contents.
      if (this.scope) {
        this.processingObserver = new MutationObserver(() => {
          if (this.scope.classList.contains('processing')) this.controller?.abort();
        });
        this.processingObserver.observe(this.scope, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      this.initialized = false;
      if (this.grid && this.button) this.setBusy(false);
      this.controller?.abort();
      this.observer?.disconnect();
      this.processingObserver?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }

    setBusy(busy) {
      this.isFetching = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) busyGrids.set(this.grid, this);
      if (busyGrids.get(this.grid) === this) {
        this.grid.setAttribute('aria-busy', String(busy));
        if (!busy) busyGrids.delete(this.grid);
      }
      this.button.setAttribute('aria-disabled', String(busy));
      this.button.classList.toggle('loading', busy);
      this.querySelector('.loading-overlay')?.classList.toggle('hidden', !busy);
    }

    updateNextUrl(url) {
      this.nextUrl = url || '';
      this.dataset.nextUrl = this.nextUrl;
      this.button.hidden = !this.nextUrl;
      if (this.nextUrl) this.button.href = this.nextUrl;
      else this.setStatus(this.dataset.completeLabel);
    }

    async fetchNextPage(manual = false) {
      if (this.isFetching || !this.nextUrl || !this.isConnected || this.scope?.classList.contains('processing')) return;
      const pageUrl = window.location.href;
      const grid = this.grid;
      this.controller = new AbortController();
      const controller = this.controller;
      this.observer?.unobserve(this);
      this.setBusy(true);
      this.setStatus(this.dataset.loadingLabel);
      try {
        const requestUrl = new URL(this.nextUrl, pageUrl);
        requestUrl.hash = '';
        if (requestUrl.origin !== window.location.origin || this.loadedUrls.has(requestUrl.href)) throw new Error('Invalid next page');
        const response = await fetch(requestUrl.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Pagination request failed');
        const text = await response.text();
        // Never append an old collection response after filtering, sorting or editor replacement.
        if (controller.signal.aborted || !this.isConnected || window.location.href !== pageUrl ||
            this.scope?.classList.contains('processing') || document.getElementById(this.gridId) !== grid ||
            !this.pagination.contains(this)) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const incomingGrid = html.getElementById(this.gridId);
        const incomingPagination = html.getElementById(this.paginationId);
        if (!incomingGrid || !incomingPagination) throw new Error('Pagination markup missing');
        const next = incomingPagination.querySelector('infinite-scroll');
        let nextUrl = '';
        if (next?.dataset.nextUrl) {
          const candidate = new URL(next.dataset.nextUrl, requestUrl);
          candidate.hash = '';
          if (candidate.origin !== window.location.origin || candidate.href === requestUrl.href || this.loadedUrls.has(candidate.href)) throw new Error('Repeated next page');
          nextUrl = candidate.href;
        }
        const items = Array.from(incomingGrid.children).filter(item =>
          item.classList.contains('card') &&
          !(this.gridId === 'product-grid-ajax' && item.classList.contains('card--banner'))
        );
        if (!items.length && nextUrl) throw new Error('Empty next page');
        const fragment = document.createDocumentFragment();
        for (const item of items) fragment.append(item);
        const firstLink = fragment.querySelector('a[href]:not([aria-hidden="true"]):not([tabindex="-1"])');
        const hadFocus = this.contains(document.activeElement);
        // Keep the existing list and control nodes so focus and the current scroll position remain stable.
        grid.append(fragment);
        this.loadedUrls.add(requestUrl.href);
        if (hadFocus && (manual || !nextUrl)) {
          if (firstLink) firstLink.focus({ preventScroll: true });
          else if (this.status) {
            this.status.hidden = false;
            this.status.tabIndex = -1;
            this.status.focus({ preventScroll: true });
          }
        }
        this.updateNextUrl(nextUrl);
        this.dataset.pageLabel = next?.dataset.pageLabel || this.dataset.completeLabel;
        this.setStatus(this.dataset.pageLabel);
        this.saveForBackNavigation();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          // Pause automatic retries; the native next-page link remains available as a retry control.
          this.automaticPaused = true;
          this.setStatus(this.dataset.errorLabel);
        }
      } finally {
        this.setBusy(false);
        if (this.isConnected && this.nextUrl && !this.automaticPaused) this.observer?.observe(this);
      }
    }

    saveForBackNavigation() {
      if (!this.loadedUrls.size || !this.isConnected || this.scope?.classList.contains('processing')) return;
      try {
        const content = this.grid.innerHTML;
        if (content.length > 1000000) return;
        sessionStorage.setItem('nosht-infinite-scroll:' + this.gridId, JSON.stringify({
          url: window.location.href, savedAt: Date.now(), mode: this.dataset.triggerType,
          initialNextUrl: this.initialNextUrl, initialCount: this.initialCount,
          content, nextUrl: this.nextUrl, loadedUrls: [...this.loadedUrls],
          pageLabel: this.dataset.pageLabel, scrollY: window.scrollY
        }));
      } catch (_) { /* Storage availability must never affect loading. */ }
    }

    restoreForBackNavigation() {
      if (restoredGrids.has(this.gridId)) return;
      restoredGrids.add(this.gridId);
      if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return;
      try {
        const cached = JSON.parse(sessionStorage.getItem('nosht-infinite-scroll:' + this.gridId));
        if (!cached || cached.url !== window.location.href || cached.mode !== this.dataset.triggerType ||
            cached.initialNextUrl !== this.initialNextUrl || cached.initialCount !== this.initialCount ||
            Date.now() - cached.savedAt > 10 * 60 * 1000) return;
        this.grid.innerHTML = cached.content;
        this.loadedUrls = new Set(cached.loadedUrls);
        this.updateNextUrl(cached.nextUrl);
        this.dataset.pageLabel = cached.pageLabel;
        this.setStatus(cached.pageLabel);
        requestAnimationFrame(() => { if (this.isConnected) window.scrollTo(0, cached.scrollY); });
      } catch (_) { /* Fall back to the server-rendered page. */ }
    }
  }
  customElements.define('infinite-scroll', InfiniteScroll);
})();
